import type { DeviceKeyBindingPayload, DeviceRegistrationAttestation, IdentityId, PublicKey, SignedRequest } from '@shared/types';
import { createLegacySignatureMessage, createSignatureMessage } from '@shared/signatureMessage.ts';
import { base64ToBytes } from '../utils/encoding';
import { verifyEd25519 } from './ed25519Provider';
import type { SigningKey } from './ed25519Provider';
import { signRequest, signRequestWithKey } from './signing';
import { logChatCrypto } from '../utils/chatCryptoDebug';

export async function signDeviceKeyBinding(params: {
  identityId: IdentityId;
  devicePublicKey: PublicKey;
  deviceEncryptionPublicKey: PublicKey;
  devicePrivateKey: Uint8Array;
  devicePrivateKeyJwk?: JsonWebKey;
}): Promise<SignedRequest<DeviceKeyBindingPayload, string>> {
  return signRequest(
    'device:key-binding',
    {
      version: 1,
      purpose: 'device-key-binding-v1',
      identityId: params.identityId,
      devicePublicKey: params.devicePublicKey,
      deviceEncryptionPublicKey: params.deviceEncryptionPublicKey
    },
    params.devicePublicKey.value,
    params.devicePrivateKey,
    params.devicePrivateKeyJwk
  );
}

export async function signDeviceKeyBindingWithKey(params: {
  identityId: IdentityId;
  devicePublicKey: PublicKey;
  deviceEncryptionPublicKey: PublicKey;
  deviceSigningKey: SigningKey;
}): Promise<SignedRequest<DeviceKeyBindingPayload, string>> {
  return signRequestWithKey(
    'device:key-binding',
    {
      version: 1,
      purpose: 'device-key-binding-v1',
      identityId: params.identityId,
      devicePublicKey: params.devicePublicKey,
      deviceEncryptionPublicKey: params.deviceEncryptionPublicKey
    },
    params.devicePublicKey.value,
    params.deviceSigningKey
  ) as Promise<SignedRequest<DeviceKeyBindingPayload, string>>;
}

export async function verifySignedRequestWithPublicKey<T>(
  request: SignedRequest<T>,
  publicKey: PublicKey
): Promise<boolean> {
  if (publicKey.algorithm !== 'ed25519') return false;
  const { signature, ...unsigned } = request;
  const message = createSignatureMessage(unsigned);
  const signatureBytes = base64ToBytes(signature);
  const publicKeyBytes = base64ToBytes(publicKey.value);
  const canonicalValid = await verifyEd25519(
    new TextEncoder().encode(message),
    signatureBytes,
    publicKeyBytes
  );
  if (canonicalValid) return true;
  const legacyMessage = createLegacySignatureMessage(unsigned);
  if (legacyMessage === message) return false;
  return verifyEd25519(
    new TextEncoder().encode(legacyMessage),
    signatureBytes,
    publicKeyBytes
  );
}

export async function verifyDeviceRegistrationAttestation(params: {
  attestation: DeviceRegistrationAttestation | null | undefined;
  identityId: IdentityId;
  identityPublicKey: PublicKey;
  devicePublicKey: PublicKey;
  deviceEncryptionPublicKey: PublicKey | null | undefined;
}): Promise<boolean> {
  const { attestation, identityId, identityPublicKey, devicePublicKey, deviceEncryptionPublicKey } = params;
  if (!attestation || attestation.version !== 1 || !deviceEncryptionPublicKey) {
    logChatCrypto('attestation', 'missing-prerequisite', {
      identityId,
      hasAttestation: Boolean(attestation),
      version: attestation?.version ?? null,
      hasDeviceEncryptionPublicKey: Boolean(deviceEncryptionPublicKey)
    });
    return false;
  }

  const registration = attestation.identitySignedRequest;
  const binding = attestation.deviceKeyBinding;
  if (!registration || !binding) {
    logChatCrypto('attestation', 'missing-registration-or-binding', {
      identityId,
      hasRegistration: Boolean(registration),
      hasBinding: Boolean(binding)
    });
    return false;
  }
  if (registration.type !== 'auth:register-device' || registration.signerId !== identityId) {
    logChatCrypto('attestation', 'registration-header-mismatch', {
      identityId,
      registrationType: registration.type,
      registrationSignerId: registration.signerId
    });
    return false;
  }
  if (registration.payload.devicePublicKey.value !== devicePublicKey.value) {
    logChatCrypto('attestation', 'registration-device-key-mismatch', {
      identityId,
      attestedDevicePublicKey: registration.payload.devicePublicKey.value,
      actualDevicePublicKey: devicePublicKey.value
    });
    return false;
  }
  if (registration.payload.deviceEncryptionPublicKey?.value !== deviceEncryptionPublicKey.value) {
    logChatCrypto('attestation', 'registration-encryption-key-mismatch', {
      identityId,
      attestedEncryptionKey: registration.payload.deviceEncryptionPublicKey?.value || null,
      actualEncryptionKey: deviceEncryptionPublicKey.value
    });
    return false;
  }
  if (registration.payload.deviceKeyBinding?.signature !== binding.signature) {
    logChatCrypto('attestation', 'binding-signature-link-mismatch', {
      identityId
    });
    return false;
  }

  const bindingPayload = binding.payload;
  if (
    binding.type !== 'device:key-binding' ||
    bindingPayload.version !== 1 ||
    bindingPayload.purpose !== 'device-key-binding-v1' ||
    bindingPayload.identityId !== identityId ||
    bindingPayload.devicePublicKey.value !== devicePublicKey.value ||
    bindingPayload.deviceEncryptionPublicKey.value !== deviceEncryptionPublicKey.value
  ) {
    logChatCrypto('attestation', 'binding-payload-mismatch', {
      identityId,
      bindingType: binding.type,
      bindingVersion: bindingPayload?.version ?? null,
      bindingPurpose: bindingPayload?.purpose ?? null,
      bindingIdentityId: bindingPayload?.identityId ?? null,
      bindingDevicePublicKey: bindingPayload?.devicePublicKey?.value ?? null,
      actualDevicePublicKey: devicePublicKey.value,
      bindingEncryptionKey: bindingPayload?.deviceEncryptionPublicKey?.value ?? null,
      actualEncryptionKey: deviceEncryptionPublicKey.value
    });
    return false;
  }

  const registrationSignatureValid = await verifySignedRequestWithPublicKey(registration, identityPublicKey);
  const bindingSignatureValid = await verifySignedRequestWithPublicKey(binding, devicePublicKey);
  logChatCrypto('attestation', 'signature-check', {
    identityId,
    registrationSignatureValid,
    bindingSignatureValid
  });
  return registrationSignatureValid && bindingSignatureValid;
}
